/**
 * Helpers para o `value` de campos do tipo `image` (arquivos anexados).
 *
 * No estado do formulário o valor de um campo de imagem é sempre uma string:
 * - `''` quando não há arquivos;
 * - JSON array de strings quando há um ou mais itens, ex.: `["<signed_id>", "https://..."]`.
 *
 * Cada item pode ser:
 * - `signed_id` de um blob enviado por direct upload (novo arquivo);
 * - URL `http(s)` de um arquivo já armazenado (mantém o anexo existente);
 * - data URL `data:...;base64,...` (formato legado, ainda aceito pela API).
 *
 * A API recebe os itens como array no mesmo `value`, na ordem em que aparecem.
 */

export const IMAGE_COMPONENT_TYPES = ['image'];

export function isStoredFileUrl(item: string): boolean {
  return item.startsWith('https://') || item.startsWith('http://');
}

export function isDataUrl(item: string): boolean {
  return item.startsWith('data:');
}

export function isPdfDataUrl(item: string): boolean {
  return item.startsWith('data:application/pdf');
}

export function isPdfStoredUrl(item: string): boolean {
  return (
    isStoredFileUrl(item) &&
    (item.includes('response-content-type=application%2Fpdf') ||
      /\.pdf(\?|$)/i.test(item))
  );
}

export function isSignedId(item: string): boolean {
  return item !== '' && !isStoredFileUrl(item) && !isDataUrl(item);
}

function cleanItems(items: unknown[]): string[] {
  return items
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

/**
 * Converte qualquer representação do valor de um campo de imagem
 * (string simples, JSON array em string, array, null) em lista de itens.
 */
export function parseImageAnswer(value: unknown): string[] {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return cleanItems(value);
  if (typeof value !== 'string') return [];

  const trimmed = value.trim();
  if (trimmed === '') return [];

  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return cleanItems(parsed);
    } catch {
      /* não é JSON: trata como item único */
    }
  }

  return [trimmed];
}

/**
 * `true` quando a string é o JSON array produzido por `serializeImageAnswer`
 * (usado como rede de segurança quando o tipo do campo não está disponível).
 */
export function isSerializedImageAnswer(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) return false;

  try {
    const parsed = JSON.parse(trimmed);
    return (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed.every((item) => typeof item === 'string')
    );
  } catch {
    return false;
  }
}

/** Serializa a lista de itens para o formato guardado no estado do formulário. */
export function serializeImageAnswer(items: string[]): string {
  const cleaned = cleanItems(items);
  return cleaned.length === 0 ? '' : JSON.stringify(cleaned);
}

/**
 * Valor que vai para a API no `value` de um campo de imagem:
 * array de itens quando há arquivos, ou o valor original (vazio/null) quando não há.
 */
export function toImageAnswerRequestValue<T>(value: T): string[] | T {
  const items = parseImageAnswer(value);
  return items.length === 0 ? value : items;
}
